import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SERVICE_DETAILS, FAQS } from "@/data/site";
import { usePageMeta, PageHero, Breadcrumb, SectionLabel, PrimaryButton, CTABanner } from "@/components/common";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICE_DETAILS[slug];
  usePageMeta(s ? s.title : "Service", s ? s.intro.slice(0, 155) : "");
  if (!s) return <Navigate to="/services" replace />;

  const otherSlugs = Object.keys(SERVICE_DETAILS).filter((k) => k !== slug).slice(0, 4);

  return (
    <div data-testid="service-detail-page">
      <PageHero label="Stucco Service" title={s.title} subtitle={s.tagline} image={s.image} />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: s.title }]} />

      <section className="py-24 bg-adn-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <SectionLabel>Overview</SectionLabel>
            <p className="text-lg text-zinc-700 mt-6 leading-relaxed">{s.intro}</p>

            <h3 className="text-2xl font-heading font-bold mt-12">What's included</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {s.points.map((p) => (
                <div key={p} className="flex items-center gap-3 border border-zinc-200 bg-white p-4">
                  <Check size={20} className="text-adn-red shrink-0" /><span className="font-medium text-zinc-700">{p}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-heading font-bold mt-12">Our process</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {s.process.map((p, i) => (
                <div key={i} className="border border-zinc-200 bg-adn-off p-6">
                  <div className="text-3xl font-heading font-black text-adn-red">{String(i + 1).padStart(2, "0")}</div>
                  <p className="mt-3 text-sm font-medium text-zinc-700">{p}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-adn-charcoal text-white p-8">
              <h3 className="text-2xl font-heading font-bold">Get a Free Estimate</h3>
              <p className="text-zinc-300 mt-3 text-sm leading-relaxed">Contact us today for a free, no-obligation quote on your {s.title.toLowerCase()} project.</p>
              <div className="mt-6"><PrimaryButton to="/contact" testid="svc-detail-cta" className="w-full">Request Estimate</PrimaryButton></div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="label-accent text-adn-red mb-4">Other Services</div>
                <ul className="space-y-3">
                  {otherSlugs.map((k) => (
                    <li key={k}><Link to={`/services/${k}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"><ArrowRight size={14} className="text-adn-red" /> {SERVICE_DETAILS[k].title}</Link></li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-20 bg-adn-off">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="text-3xl font-heading font-extrabold mt-6 mb-8">Common questions</h2>
          <div className="space-y-4">
            {FAQS.slice(0, 4).map((f) => (
              <div key={f.q} className="bg-white border border-zinc-200 p-6">
                <h4 className="font-heading font-bold text-lg">{f.q}</h4>
                <p className="text-zinc-600 mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTABanner />
    </div>
  );
}
